import React, { useState } from "react";
import { useGetTextsReportQuery } from "@/store/rest_api";
import { TextsReportRow } from "@/model/interface/Message";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ShieldAlert } from "lucide-react";

function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function StatTile({ label, value, note }: { label: string; value: string; note?: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-2 text-3xl font-semibold tabular-nums">{value}</p>
      {note && <div className="mt-2">{note}</div>}
    </div>
  );
}

function ReportTable({ title, rows }: { title: string; rows: TextsReportRow[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{title === "Per User" ? "User" : "Number"}</TableHead>
              <TableHead className="text-right">Sent</TableHead>
              <TableHead className="text-right">Delivered</TableHead>
              <TableHead className="text-right">Delivered %</TableHead>
              <TableHead className="text-right">Undelivered</TableHead>
              <TableHead className="text-right">Filtered</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                  No messages in this period
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell className="font-medium">
                  <span className="flex items-center gap-2">
                    {row.label}
                    {row.highFilterRate && <ShieldAlert className="h-4 w-4 text-destructive" />}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">{row.sent}</TableCell>
                <TableCell className="text-right tabular-nums">{row.delivered}</TableCell>
                <TableCell className="text-right tabular-nums">{row.deliveredRatePct}%</TableCell>
                <TableCell className="text-right tabular-nums">{row.undelivered}</TableCell>
                <TableCell className="text-right tabular-nums">{row.filtered}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function TextsReportPage() {
  const [fromDate, setFromDate] = useState(() =>
    toDateInputValue(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
  );
  const [toDate, setToDate] = useState(() => toDateInputValue(new Date()));

  const { data: report, isLoading } = useGetTextsReportQuery({
    from: new Date(`${fromDate}T00:00:00Z`).toISOString(),
    to: new Date(`${toDate}T23:59:59Z`).toISOString(),
  });

  const flaggedNumbers = report?.perNumber.filter((row) => row.highFilterRate) ?? [];

  return (
    <div className="p-4 space-y-4 text-foreground">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Texts Report</h1>
          <p className="text-muted-foreground">
            Delivery outcomes, filtering, and opt-outs across your numbers and team.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
          </div>
        </div>
      </div>

      {flaggedNumbers.length > 0 && (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>
            {flaggedNumbers.map((row) => row.label).join(", ")}{" "}
            {flaggedNumbers.length === 1 ? "has" : "have"} an unusually high carrier filter rate.
            Filtered messages usually mean weak opt-in or spam-like content; review what is being
            sent from {flaggedNumbers.length === 1 ? "this number" : "these numbers"}.
          </AlertDescription>
        </Alert>
      )}

      {isLoading || !report ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <StatTile label="Sent" value={String(report.sent)} />
            <StatTile label="Delivered" value={String(report.delivered)} />
            <StatTile label="Delivered Rate" value={`${report.deliveredRatePct}%`} />
            <StatTile label="Undelivered" value={String(report.undelivered)} />
            <StatTile label="Filtered" value={String(report.filtered)} />
            <StatTile
              label="Opt-Outs"
              value={String(report.optOuts)}
              note={
                report.possibleOptOuts > 0 ? (
                  <span className="status-pill-warning">
                    {report.possibleOptOuts} to review
                  </span>
                ) : undefined
              }
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <ReportTable title="Per User" rows={report.perUser} />
            <ReportTable title="Per Number" rows={report.perNumber} />
          </div>
        </>
      )}
    </div>
  );
}
